import React, { useEffect, useRef, useState } from "react";
import { PRIMARY_NAV, SIGNUP_URL, WEBAPP_URL, NavItem } from "../siteConfig";
import { IconChevronDown, IconClose, IconMenu, IconArrowRight } from "./Icons";
import "../styles/header.css";

type SiteHeaderProps = {
  /** Path or hash of the page currently rendered, for aria-current. */
  current?: string;
  /** Ink headers sit on dark hero sections and start transparent-on-dark. */
  tone?: "light" | "ink";
};

/* Exact match only. Hash links like "/#pricing" are in-page jumps, not pages,
   so they must never claim aria-current while sitting on "/". */
const isActive = (item: NavItem, current?: string) =>
  Boolean(current) && item.href === current;

const SiteHeader: React.FC<SiteHeaderProps> = ({ current, tone = "light" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const closeTimer = useRef<number | undefined>(undefined);

  /* Elevate the bar once the page has scrolled past the hero's first slice. */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Lock body scroll while the mobile drawer is open. */
  useEffect(() => {
    document.body.classList.toggle("is-locked", menuOpen);
    return () => document.body.classList.remove("is-locked");
  }, [menuOpen]);

  /* Escape closes whichever layer is open; outside click closes dropdowns. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setMenuOpen(false);
      setOpenDropdown(null);
    };
    const onPointer = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, []);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const openWithDelayCancel = (label: string) => {
    window.clearTimeout(closeTimer.current);
    setOpenDropdown(label);
  };

  /* Small grace period so diagonal mouse travel into the panel doesn't close it. */
  const scheduleClose = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenDropdown(null), 140);
  };

  return (
    <>
      <a className="u-skip" href="#main">
        Skip to content
      </a>

      <header
        className={[
          "hdr",
          `hdr--${tone}`,
          scrolled ? "is-scrolled" : "",
          menuOpen ? "is-open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="hdr__inner u-container">
          <a className="hdr__brand" href="/" aria-label="PABS — home">
            <img
              className="hdr__logo"
              src={tone === "ink" ? "/pabs-wordmark-white.png" : "/pabs-wordmark-violet.png"}
              alt="PABS"
              width={96}
              height={57}
            />
          </a>

          <nav className="hdr__nav" aria-label="Primary" ref={navRef}>
            {PRIMARY_NAV.map((item) =>
              item.children ? (
                <div
                  key={item.label}
                  className={`hdr__group${openDropdown === item.label ? " is-open" : ""}`}
                  onMouseEnter={() => openWithDelayCancel(item.label)}
                  onMouseLeave={scheduleClose}
                >
                  <button
                    type="button"
                    className="hdr__link hdr__link--trigger"
                    aria-expanded={openDropdown === item.label}
                    aria-haspopup="true"
                    onClick={() =>
                      setOpenDropdown(openDropdown === item.label ? null : item.label)
                    }
                  >
                    {item.label}
                    <IconChevronDown size={15} className="hdr__caret" />
                  </button>

                  <div className="hdr__panel" role="group" aria-label={item.label}>
                    <div className="hdr__panel-inner">
                      {item.children.map((child) => (
                        <a key={child.label} className="hdr__panel-item" href={child.href}>
                          <span className="hdr__panel-title">{child.label}</span>
                          <span className="hdr__panel-desc">{child.description}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <a
                  key={item.label}
                  className="hdr__link"
                  href={item.href}
                  aria-current={isActive(item, current) ? "page" : undefined}
                >
                  {item.label}
                </a>
              )
            )}
          </nav>

          <div className="hdr__actions">
            <a className="hdr__login" href={WEBAPP_URL}>
              Log in
            </a>
            <a className="btn btn--primary btn--sm hdr__cta" href={SIGNUP_URL}>
              Get started
            </a>
            <button
              type="button"
              className="hdr__burger"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              {menuOpen ? <IconClose size={22} /> : <IconMenu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer — a designed panel, not a squeezed desktop nav */}
      <div
        className={`drawer${menuOpen ? " is-open" : ""}`}
        aria-hidden={!menuOpen}
        id="mobile-menu"
      >
        <button
          type="button"
          className="drawer__scrim"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => setMenuOpen(false)}
        />

        <div className="drawer__sheet" role="dialog" aria-modal="true" aria-label="Menu">
          <nav className="drawer__nav">
            {PRIMARY_NAV.map((item, i) => (
              <div className="drawer__block" key={item.label} style={{ ["--i" as string]: i }}>
                <a
                  className="drawer__link"
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                  <IconArrowRight size={18} />
                </a>
                {item.children ? (
                  <div className="drawer__sub">
                    {item.children.map((child) => (
                      <a
                        key={child.label}
                        className="drawer__sublink"
                        href={child.href}
                        onClick={() => setMenuOpen(false)}
                      >
                        {child.label}
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </nav>

          <div className="drawer__foot">
            <a className="btn btn--primary btn--block btn--lg" href={SIGNUP_URL}>
              Get started
            </a>
            <a className="btn btn--secondary btn--block" href={WEBAPP_URL}>
              Log in
            </a>
            <a className="drawer__minor" href="/download">
              Download the PABS app
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default SiteHeader;
